<template>
  <div class="overflow-hidden">
    <v-container class="lg:flex">
      <v-col
        class="p-0"
        cols="24"
        lg="12"
        xl="14"
        xxl="14"
      >
        <div class="max-w-[450px] -ml-5 mb-4">
          <trustpilot-widget
            template-id="5419b6ffb0d04a076446a9af"
            style-height="20px"
            style-width="100%"
            class="text-left"
          />
        </div>

        <u-text
          color="type-primary"
          class="max-lg:text-center text-heading-h2 mb-4 lg:mb-8 xl:mb-10"
          tag="h1"
        >
          <u-content :document="blok.title" />
        </u-text>

        <u-text
          class="max-lg:text-center text-20 lg:text-32 mb-6 lg:mb-10"
          text="Get expertly matched to one of 27,000+ vetted advisers in as quick as 36 minutes."
        />

        <template v-if="mdAndDown">
          <div class="grid gap-1 items-center justify-center mb-15 mt-6">
            <u-text
              class="font-bold text-center"
              color="#50505099"
              text="As featured by"
            />
            <partner-logo logo="GovUk" />
          </div>
        </template>

        <v-sheet
          class="p-6 bg-secondary-putty flex flex-col items-center"
          rounded="4"
        >
          <u-text
            class="text-28 font-bold"
            text="Get matched now"
          />
          <u-text
            class="text-body-xs mb-8"
            text="Take our 2 minute quiz"
          />
          <u-text
            color="type-secondary"
            class="text-center text-body mb-4"
          >
            <u-content :document="blok.subtitle" />
          </u-text>

          <div class="grid gap-4 max-sm:w-full md:grid-cols-2 xl:w-9/12 xxl:w-8/12">
            <template
              v-for="(btn, index) in heroBtns"
              :key="index"
            >
              <u-btn
                size="large"
                :text="btn.text"
                v-on="btn.on"
              />
            </template>

            <trustpilot-custom class="sm:max-lg:col-span-2" />

            <div
              v-if="lgAndUp"
              class="text-center space-y-2"
            >
              <u-text
                class="text-body-xxs"
                text="As featured by"
              />
              <partner-logo
                logo="GovUk"
                class="mx-auto"
              />
            </div>
          </div>
        </v-sheet>
      </v-col>

      <template v-if="mdAndDown">
        <partners-section
          :partners="['BBC', 'MoneySavingExpert', 'FinancialTimes']"
          class="mt-8 mb-12"
        />
      </template>

      <v-col
        offset-lg="1"
        class="p-0 max-lg:-mx-[calc(40px+var(--container-padding-x,16px))] max-lg:max-w-none max-lg:w-auto"
      >
        <home-hero />
      </v-col>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { UBtn } from '#components'
import type { HomeLayoutStoryblok } from '#shared/types/storyblok-component-types'

defineProps<{
  blok: HomeLayoutStoryblok
}>()

const { v2Url } = useRuntimeConfig().public
const { mdAndDown, lgAndUp } = useDisplay()
const { trackEvent } = useGtm()
const heroBtns = computed(() => ([
  {
    text: 'Financial adviser',
    on: {
      click: () => {
        trackQuora()
        trackEvent({
          event: 'gaEvent',
          eventAction: 'click',
          eventCategory: 'homepage',
          eventLabel: 'homepage_fifa_cta',
        })
        goToMatchIntroPage('financial-adviser')
      },
    },
  },
  {
    text: 'Mortgage broker',
    on: {
      click: () => {
        trackQuora()
        trackEvent({
          event: 'gaEvent',
          eventAction: 'click',
          eventCategory: 'homepage',
          eventLabel: 'homepage_fama_cta',
        })
        goToMatchIntroPage('mortgage-adviser', 'mortgages')
      },
    },
  },
  {
    text: 'Insurance broker',
    on: {
      click: () => {
        trackQuora()
        trackEvent({
          event: 'gaEvent',
          eventAction: 'click',
          eventCategory: 'homepage',
          eventLabel: 'homepage_insurance_cta',
        })
        goToMatchIntroPage('financial-adviser', 'insurance-and-protection')
      },
    },
  },
  {
    text: 'Accountant',
    on: {
      click: () => {
        trackQuora()
        trackEvent({
          event: 'gaEvent',
          eventAction: 'click',
          eventCategory: 'homepage',
          eventLabel: 'homepage_faa_cta',
        })
        goToMatchIntroPage('accountant')
      },
    },
  },
]))

function goToMatchIntroPage(serviceType: string, primaryAdviceArea: string | null = null) {
  const url = `${v2Url}/enquiry/match-intro/${serviceType}`

  return navigateTo({
    path: url,
    query: {
      ...(primaryAdviceArea !== null && { primaryAdviceArea }),
    },
  }, {
    external: true,
  })
}

function trackQuora() {
  if ((window as any).qp) {
    qp('track', 'CompleteRegistration')
  }
}
</script>
